// ABOUTME: Warehouse-side action buttons that change a support ticket's status.
// ABOUTME: Renders Resolve / Reopen / Close based on the current status; each opens a confirmation.

import { Box, useTheme } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import Shbutton from "../../../../../components/theme/buttons/Shbutton";
import MConfirmation from "../../../../../components/global/Modals/MConfirmation";
import { useUpdateSupportTicketMutation } from "../../../../../newapi/warehouse/supportTicketApiSlice";
import { support_ticket_status } from "../../../../../utils/staticEnums";
import {
    showToastMessageUpdateRequest,
    showToastMessageRequestError,
} from "../../../../../utils/toasts";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ReplayIcon from "@mui/icons-material/Replay";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

const TicketStatusActions = ({ ticket, onStatusChange, relationMode }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const primaryColor = theme.palette.mode === "dark" ? "blue" : "primary";
    const greyColor = theme.palette.mode === "dark" ? "primaryGrey" : "grey";
    const [updateTicket] = useUpdateSupportTicketMutation();
    const [pendingTarget, setPendingTarget] = useState(null);

    if (!ticket?.id) return null;
    if (ticket.status === support_ticket_status.CLOSED) return null;
    // Relation users can only resolve; once resolved there is nothing left
    // for them here (replying is their way to reopen).
    if (relationMode && ticket.status === support_ticket_status.RESOLVED) return null;

    const askFor = (target) => () => setPendingTarget(target);

    const confirm = async () => {
        if (!pendingTarget) return;
        try {
            const result = await updateTicket({
                id: ticket.id,
                data: { status: pendingTarget },
            }).unwrap();
            showToastMessageUpdateRequest(t, result);
            onStatusChange?.(pendingTarget);
        } catch (err) {
            showToastMessageRequestError(t("update_failed"), t, err);
        }
    };

    const isResolved = ticket.status === support_ticket_status.RESOLVED;

    const CONFIRM_COPY = {
        [support_ticket_status.RESOLVED]: {
            title: t("resolve_ticket"),
            subtitle: t("resolve_ticket_subtitle"),
        },
        [support_ticket_status.IN_PROGRESS]: {
            title: t("reopen_ticket"),
            subtitle: t("reopen_ticket_subtitle"),
        },
        [support_ticket_status.CLOSED]: {
            title: t("close_ticket"),
            subtitle: t("close_ticket_subtitle"),
        },
    };

    const copy = pendingTarget ? CONFIRM_COPY[pendingTarget] : { title: "", subtitle: "" };

    return (
        <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {isResolved ? (
                    <>
                        <Shbutton
                            size={"32"}
                            variant="contained"
                            color={greyColor}
                            startIcon={<ReplayIcon />}
                            onClick={askFor(support_ticket_status.IN_PROGRESS)}
                        >
                            {t("reopen")}
                        </Shbutton>
                        <Shbutton
                            size={"32"}
                            variant="contained"
                            color={primaryColor}
                            startIcon={<LockOutlinedIcon />}
                            onClick={askFor(support_ticket_status.CLOSED)}
                        >
                            {t("close")}
                        </Shbutton>
                    </>
                ) : (
                    <Shbutton
                        size={"32"}
                        variant="contained"
                        color={theme.palette.mode === "dark" ? "blue" : "primary"}
                        startIcon={<CheckCircleOutlineIcon />}
                        onClick={askFor(support_ticket_status.RESOLVED)}
                    >
                        {t("resolved")}
                    </Shbutton>
                )}
            </Box>
            <MConfirmation
                open={!!pendingTarget}
                handleClose={() => setPendingTarget(null)}
                confirm={confirm}
                title={copy.title}
                subtitle={copy.subtitle}
            />
        </>
    );
};

export default TicketStatusActions;
